import type { EditorStyleActionKey } from "./editor-style-actions.js";
import type { DataSourceId } from "../layout/types.js";

// ── Port ─────────────────────────────────────────────────────────────────────

type PortDirection = "input" | "output";

export interface Port {
  id: string;
  label: string;
  direction: PortDirection;
  valueType: string;
  value?: PortValue;
  /** Optional object shape; `valueType` remains object/list for old runtimes. */
  objectTypeId?: string;
}

// ── Value types ───────────────────────────────────────────────────────────────

export interface LocationValue {
  x: number;
  y: number;
  z: number;
}

export interface TransformValue {
  width: number;
  height: number;
  rotation: number;
}

export interface BorderValue {
  border: {
    top: string;
    right: string;
    bottom: string;
    left: string;
  };
}

export interface BorderRadiusValue {
  radius: {
    topLeft: string;
    topRight: string;
    bottomRight: string;
    bottomLeft: string;
  };
}

export interface SpacingValue {
  margin: {
    scope: "all" | "top" | "right" | "bottom" | "left";
    value: number;
    sides?: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
  padding: {
    scope: "all" | "top" | "right" | "bottom" | "left";
    value: number;
    sides?: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
}

export interface NodePortValue {
  component: "barLoader";
  weight: number | "all";
  part: "disk" | "label";
}

export interface LayoutListTargetValue {
  kind: "layoutList";
  nodeId: string;
}
export interface LayoutListTargetOption {
  nodeId: string;
  label: string;
}

export type ListValue = unknown[];

export const FONT_WEIGHT_VALUES = [
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;

export type FontWeightValue = (typeof FONT_WEIGHT_VALUES)[number];

export const FONT_FAMILY_VALUES = [
  "Arial",
  "Arial Black",
  "Arial Narrow",
  "Arial Rounded MT Bold",
  "Avenir",
  "Avenir Next",
  "Baskerville",
  "Bodoni MT",
  "Book Antiqua",
  "Brush Script MT",
  "Calibri",
  "Cambria",
  "Candara",
  "Century Gothic",
  "Charter",
  "Comic Sans MS",
  "Consolas",
  "Copperplate",
  "Courier",
  "Courier New",
  "Cursive",
  "Didot",
  "Fantasy",
  "Franklin Gothic Medium",
  "Futura",
  "Garamond",
  "Verdana",
  "Geneva",
  "Georgia",
  "Gill Sans",
  "Helvetica",
  "Helvetica Neue",
  "Hoefler Text",
  "Impact",
  "Inter",
  "Lato",
  "Lucida Console",
  "Lucida Sans Unicode",
  "Menlo",
  "Merriweather",
  "Monaco",
  "Monospace",
  "Montserrat",
  "MS Sans Serif",
  "MS Serif",
  "Noto Sans",
  "Noto Serif",
  "Nunito",
  "Open Sans",
  "Optima",
  "Oswald",
  "Palatino Linotype",
  "Poppins",
  "PT Sans",
  "PT Serif",
  "Raleway",
  "Roboto",
  "Roboto Slab",
  "Rockwell",
  "Sans-serif",
  "Segoe UI",
  "Segoe UI Light",
  "Segoe UI Semibold",
  "Serif",
  "Source Sans Pro",
  "Source Serif Pro",
  "Symbol",
  "System UI",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Webdings",
  "Wingdings",
] as const;

/** Built-in web fonts or a user-uploaded custom family name. */
export type FontFamilyValue = (typeof FONT_FAMILY_VALUES)[number] | string;

export const TEXT_ALIGN_VALUES = ["left", "center", "right"] as const;

export type TextAlignValue = (typeof TEXT_ALIGN_VALUES)[number];

/** CSS `text-transform` keyword values supported by layout styles and behavior literals. */
export const TEXT_TRANSFORM_VALUES = [
  "none",
  "capitalize",
  "uppercase",
  "lowercase",
] as const;

export type TextTransformValue = (typeof TEXT_TRANSFORM_VALUES)[number];

/** CSS `white-space` keyword values supported by layout styles and behavior literals. */
export const WHITE_SPACE_VALUES = [
  "normal",
  "pre",
  "pre-wrap",
  "pre-line",
  "nowrap",
  "wrap",
  "break-spaces",
  "collapse",
  "preserve nowrap",
] as const;

export type WhiteSpaceValue = (typeof WHITE_SPACE_VALUES)[number];

/** CSS `text-decoration-line` keywords (space-separated when multiple). */
export const TEXT_DECORATION_LINE_VALUES = [
  "none",
  "underline",
  "overline",
  "line-through",
  "blink",
  "spelling-error",
  "grammar-error",
] as const;

export type TextDecorationLineValue =
  (typeof TEXT_DECORATION_LINE_VALUES)[number];

export const TEXT_DECORATION_STYLE_VALUES = [
  "solid",
  "double",
  "dotted",
  "dashed",
  "wavy",
] as const;

export type TextDecorationStyleValue =
  (typeof TEXT_DECORATION_STYLE_VALUES)[number];

export type CompositeValueType =
  | "location"
  | "transform"
  | "color"
  | "gradient"
  | "filter"
  | "boxShadow"
  | "textShadow"
  | "border"
  | "borderRadius"
  | "spacing";

export type VariableValueType =
  | "number"
  | "string"
  | "boolean"
  | "list"
  | "object"
  | "date"
  | "fontFamily"
  | "fontWeight"
  | "textAlign"
  | "textTransform"
  | "whiteSpace"
  | "textDecorationLine"
  | "textDecorationColor"
  | "textDecorationStyle"
  | "textDecorationThickness"
  | "animationDirection"
  | "animationFill"
  | "animationEasing"
  | "any"
  | "node"
  | "layoutListTarget"
  | CompositeValueType;

export type PortValue =
  | string
  | number
  | boolean
  | ListValue
  | LocationValue
  | TransformValue
  | BorderValue
  | BorderRadiusValue
  | SpacingValue
  | NodePortValue
  | LayoutListTargetValue;

/** Value types that operators can consume as direct inputs. */
export type PrimitiveValueType =
  | "number"
  | "string"
  | "boolean"
  | "list"
  | "object"
  | "date"
  | "fontFamily"
  | "fontWeight"
  | "textAlign"
  | "textTransform"
  | "whiteSpace"
  | "textDecorationLine"
  | "textDecorationColor"
  | "textDecorationStyle"
  | "textDecorationThickness"
  | "color"
  | "gradient"
  | "filter"
  | "boxShadow"
  | "textShadow"
  | "border"
  | "borderRadius"
  | "spacing"
  | "location"
  | "transform"
  | "animationDirection"
  | "animationFill"
  | "animationEasing";

// ── Operator taxonomy ─────────────────────────────────────────────────────────

/** Unary operators: 1 input → 1 output (same or boolean). */
export type UnaryOperator =
  | "readTimer"
  | "constructObject"
  | "objectKeys"
  | "objectValues"
  | "objectEntries"
  | "defined"
  | "empty"
  | "not"
  | "get"
  | "length"
  | "isNotEmpty"
  /** Adds every finite numeric value in a list. */
  | "sum"
  /** Returns the arithmetic mean of every finite numeric value in a list. */
  | "average"
  /** Returns the smallest finite numeric value in a list. */
  | "min"
  /** Returns the largest finite numeric value in a list. */
  | "max"
  | "factorial"
  | "ceil"
  /**
   * `currentItem(list)` - returns the current iterated element when used inside
   * a `findByCondition` predicate (or any per-item iteration scope that
   * injects `layoutListRowItem` into the runtime context). Outside of such an
   * iteration scope, returns `undefined`.
   *
   * The output type is inferred from the connected list (same item type as
   * `find` / `findByCondition`).
   */
  | "currentItem"
  | "currentEntry"
  /**
   * `comparator(list)` - node with two output ports (`outputA` / `outputB`)
   * exposing the pair of items currently being compared by `sort`. Reads
   * `iterationItemA` / `iterationItemB` from the runtime context.
   *
   * The output type of both ports is inferred from the connected list (same
   * item type as `currentItem`).
   */
  | "comparator"
  | "castToString"
  | "castToFontFamily"
  | "castToTextAlign"
  | "castToTextTransform"
  | "castToWhiteSpace"
  | "castToTextDecorationLine"
  | "castToTextDecorationStyle"
  | "castToNumber"
  | "castToBoolean"
  | "castToFontWeight"
  | "castToColor"
  | "castToGradient"
  | "castToFilter"
  | "castToBoxShadow"
  | "castToTextShadow"
  | "castToBorder"
  | "castToBorderRadius"
  | "castToObject"
  | DateGetOperator
  | StringUnaryOperator;

/** Number comparison operators: 2 number inputs → boolean output. */
export type NumberComparisonOperator =
  | "equals"
  | "notEquals"
  | "greaterThan"
  | "lessThan"
  | "greaterThanOrEqual"
  | "lessThanOrEqual";

/** Arithmetic operators: 2 number inputs → number output. */
export type NumberArithmeticOperator =
  | "add"
  | "subtract"
  | "multiply"
  | "divide"
  | "modulo"
  | "power"
  | "round";

/** String comparison operators: 2 string inputs → boolean output. */
export type StringComparisonOperator =
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "equals"
  | "notEquals";

export type StringUnaryOperator = "toUpperCase" | "toLowerCase" | "capitalize";

export type DateBinaryOperator =
  | "dateDifference"
  | "isSame"
  | "isAfter"
  | "isBefore"
  | "addDays"
  | "addMonths"
  | "addYears"
  | "addHours"
  | "addMinutes"
  | "addSeconds";

export type DateDifferencePart = "years" | "months" | "days";

export type DateGetOperator =
  | "getDay"
  | "getMonth"
  | "getYear"
  | "getHour"
  | "getMinute"
  | "getSecond";

/** Color operators. */
export type ColorComparisonOperator = "equals" | "notEquals";
export type ColorUnaryOperator = "withAlpha";
export type ColorTernaryOperator = "mix";
export type BooleanTernaryOperator = "boolToSign";
export type PrimitiveTernaryOperator = "ifElse";

/** String transform operators. */
export type StringTransformOperator = "concat" | "chartAt";
/** String comparator operator: 2 string inputs → comparator sign number. */
export type StringComparatorOperator = "compare";

/** List operators. */
export type ListBinaryOperator =
  | "mapItems"
  | "join"
  | "push"
  | "remove"
  /** Returns at most the first N items without mutating the input list. */
  | "take"
  | "get"
  | "map"
  | "find"
  /**
   * Groups list objects by a dot-path and returns `{ key, items }` entries in
   * first-seen key order.
   */
  | "groupBy"
  /**
   * Enriches `{ key, items }` groups with numeric statistics for an expression
   * evaluated once per item:
   * `{ key, items, count, sum, average, min, max }`.
   */
  | "aggregateGroups"
  /**
   * `findByCondition(list, predicate)` - first element where the boolean
   * `predicate` evaluates to true. The predicate is re-evaluated per item;
   * the upstream graph references the current item via the `currentItem`
   * operator (which reads `iterationItem` from the scoped context).
   */
  | "findByCondition"
  /**
   * `filter(list, predicate)` - every element where the boolean `predicate`
   * evaluates to true, in original order. Same per-item iteration scope as
   * `findByCondition` (`iterationItem` / `iterationIndex`).
   */
  | "filter"
  /**
   * `sort(list, comparator)` - list sorted by a numeric `comparator`
   * expression evaluated per pair (negative ⇒ A before B, zero ⇒ equal,
   * positive ⇒ B before A). The comparator graph references the two
   * compared items via the `comparator` operator (which exposes
   * `iterationItemA` / `iterationItemB` from the scoped context).
   */
  | "sort"
  /** Stable lexicographic sort; key expression is evaluated once per currentItem. */
  | "sortBy";
/** Object operators. */
export type ObjectBinaryOperator =
  | "getByPath"
  | "extendObject"
  | "pickObject"
  | "omitObject"
  | "mapValues";

/** Logical operators: 2 boolean inputs → boolean output. */
export type LogicalOperator = "and" | "or";

/**
 * Ternary operators: 3 inputs → 1 output.
 * - `replace`: `replace(source, search, replacement)` - replaces all
 *   occurrences of `search` in `source` with `replacement`.
 * - `boolToSign`: `boolToSign(condition, whenTrue, whenFalse)` - returns
 *   `whenTrue` when condition is truthy, otherwise `whenFalse`.
 */
export type TernaryOperator =
  | "setProperty"
  | "replace"
  | ColorTernaryOperator
  | BooleanTernaryOperator
  | PrimitiveTernaryOperator
  /** First matching list element by dot-path and expected value (both string ports). */
  | "findByPath";

// Convenience aliases grouping related operators
export type NumberOperator =
  | NumberComparisonOperator
  | NumberArithmeticOperator;
export type StringOperator =
  | StringComparisonOperator
  | StringTransformOperator
  | StringComparatorOperator;
export type BinaryOperator =
  | NumberOperator
  | StringOperator
  | ListBinaryOperator
  | ObjectBinaryOperator
  | LogicalOperator
  | DateBinaryOperator
  | ColorComparisonOperator
  | ColorUnaryOperator;

export type Operator = UnaryOperator | BinaryOperator | TernaryOperator;

export type DatePartTextFormat =
  | "numeric"
  | "narrow"
  | "short"
  | "long"
  | "2-digit";

/** Intl format options selectable in the blueprint UI for {@link DateGetOperator} `getDay` / `getMonth`. */
export const DATE_PART_TEXT_FORMAT_CHOICES: readonly DatePartTextFormat[] = [
  "numeric",
  "narrow",
  "short",
  "long",
  "2-digit",
];

// ── Node interfaces ───────────────────────────────────────────────────────────

export type NodeType = "operator" | "variable" | "event" | "control" | "action";

export interface BaseNode {
  id: string;
  x: number;
  y: number;
  type: NodeType;
  ports: Port[];
}

export interface OperatorNode extends BaseNode {
  type: "operator";
  operator: Operator;
  mode: "unary" | "binary" | "ternary";
  objectSource?: string;
  objectFieldPathBase?: string;
  objectFieldPath?: string;
  listItemValueType?: Exclude<VariableValueType, "list">;
  ports: Port[];
  datePartTextFormat?: DatePartTextFormat;
  objectTypeId?: string;
  objectTypeName?: string;
  /** Editable definition body rather than a call to a scene object factory. */
  objectDefinition?: boolean;
}

export interface UnaryOperatorNode extends OperatorNode {
  mode: "unary";
  operator: UnaryOperator;
  /**
   * Most unary operators expose a single `output` port. The exception is
   * `comparator`, which exposes two output ports (`outputA` / `outputB`)
   * for the pair of items currently being compared by `sort`.
   */
  ports:
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "output"; direction: "output" },
      ]
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "outputA"; direction: "output" },
        Port & { id: "outputB"; direction: "output" },
      ];
}

export interface BinaryOperatorNode extends OperatorNode {
  mode: "binary";
  operator: BinaryOperator;
  /**
   * Most binary operators expose a single `output` port. The exceptions are
   * the list-search operators `find` / `findByCondition`, which expose two
   * output ports: `output` (the matched item, typed as the list item) and
   * `outputIndex` (the matched 0-based index, `number`, `-1` if not found).
   * `dateDifference` exposes three numeric outputs: whole years, remaining months and days.
   */
  ports:
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "input2"; direction: "input" },
        Port & { id: "output"; direction: "output" },
      ]
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "input2"; direction: "input" },
        Port & { id: "output"; direction: "output" },
        Port & { id: "outputIndex"; direction: "output" },
      ]
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "input2"; direction: "input" },
        Port & { id: "outputYears"; direction: "output" },
        Port & { id: "outputMonths"; direction: "output" },
        Port & { id: "outputDays"; direction: "output" },
      ];
}

/**
 * Ternary operator node: 3 typed input ports → 1 (or 2) output ports.
 * The 2-output shape is used by `findByPath` (item + index).
 */
export interface TernaryOperatorNode extends OperatorNode {
  mode: "ternary";
  operator: TernaryOperator;
  ports:
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "input2"; direction: "input" },
        Port & { id: "input3"; direction: "input" },
        Port & { id: "output"; direction: "output" },
      ]
    | [
        Port & { id: "input1"; direction: "input" },
        Port & { id: "input2"; direction: "input" },
        Port & { id: "input3"; direction: "input" },
        Port & { id: "output"; direction: "output" },
        Port & { id: "outputIndex"; direction: "output" },
      ];
}

export interface VariableNode extends BaseNode {
  type: "variable";
  valueType: VariableValueType;
  listItemValueType?: Exclude<VariableValueType, "list">;
  objectSource?: string;
  objectFieldPath?: string;
  nodeWeights?: number[];
  nodeUnit?: "kg" | "lbs";
  ports: Port[];
}

/**
 * References a scene-level variable from the catalog.
 * Has value-only input ports (never flow) and a single typed output.
 */
export interface VariableRefNode extends BaseNode {
  type: "variable";
  valueType: "variableRef";
  variableId: string;
  ports: Port[];
}

/**
 * Discriminant for the event trigger strategy.
 * - `onChange`: fires when a watched {@link InjectedData} field value changes.
 * - `onLoad`: fires once when the layout node mounts for the first time.
 * - `onListEnter`: fires for each row entering a layoutList (new key detected).
 * - `onListExit`: fires for each row leaving a layoutList (key no longer present).
 * - `onListFilterExit`: fires when a watched layoutList filter key changes before row swap.
 * - `onListFilterEnter`: fires when a watched layoutList filter key changes after row swap.
 */
export type EventKind =
  | "onChange"
  | "onLoad"
  | "onClick"
  | "onListEnter"
  | "onListExit"
  | "onListFilterExit"
  | "onListFilterEnter"
  | "onControlRoom"
  | "onCustomSceneEvent";

export interface EventNode extends BaseNode {
  type: "event";
  kind: EventKind;
  /** When true, `onChange` also fires on first runtime evaluation (no previousData). */
  triggerOnInit?: boolean;
  /**
   * Data source to watch. Required for `onChange` events; absent for `onLoad`
   * since it is not driven by any data field.
   */
  objectSource?: string;
  objectFieldPath?: string;
  /**
   * Optional list target used by list filter transition events.
   * If omitted, the owner layoutList is used when available.
   */
  target?: LayoutListTargetValue;

  // ── Control Room (régie) fields ─────────────────────────────────────────────
  /** Human-readable name shown in the Control Room card (required for publishability). */
  signalName?: string;
  /** Human-readable description shown in the Control Room card (required for publishability). */
  signalDescription?: string;
  /** First key of the 1-to-3 key shortcut (required for publishability). */
  shortcutKey1?: string;
  /** Optional second key of the shortcut combo. */
  shortcutKey2?: string;
  /** Optional third key of the shortcut combo. */
  shortcutKey3?: string;
  /** Optional operator preference key, resolved by hosts that provide keyboard settings. */
  shortcutBinding?: string;
  /** Action key whose tooltip should advertise this shortcut (defaults to its owner action). */
  shortcutActionKey?: string;

  /** Reference to a SceneCustomEventEntry.id when kind is `onCustomSceneEvent`. */
  customEventId?: string;

  ports: Port[];
}

export interface ControlNode extends BaseNode {
  type: "control";
  controlType:
    | "ifElse"
    | "wait"
    | "repeat"
    | "mergeFlows"
    | "eventOutput"
    | "functionBegin"
    | "functionEnd"
    | "variableBegin"
    | "variableEnd";
  dataValueType: string;
  ports: Port[];
}

/**
 * Key of the field affected by a generic "set" action node.
 */
export type ActionSetKey =
  | import("../behavior-timers/protocol.js").TimerAction
  | "text"
  | "location"
  | "transform"
  /** Sets `LayoutList` row filter predicate (layout-list node behavior only; boolean input graph). */
  | "setFilter"
  /** Sets `LayoutList` row sort comparator (layout-list node behavior only; number input graph). */
  | "setSort"
  /** Replaces a `LayoutList` bound source with a computed list. */
  | "setListSource"
  /** Sets `BarLoaderProps.totalWeight` (bar-loader node behavior only; number input). */
  | "totalWeight"
  /** Sets `BarLoaderProps.barWeight` (bar-loader node behavior only; number input). */
  | "barWeight"
  /** Sets a data binding (layout-list node behavior only; string input). */
  | "setBinding"
  /** MUI icon color (icon node behavior only; color input). */
  | "iconColor"
  /** MUI icon rendered size in pixels (icon node behavior only; number input). */
  | "iconSize"
  /** Custom discipline action button label. */
  | "customActionLabel"
  /** Custom discipline action button disabled state. */
  | "customActionDisabled"
  /** Custom discipline action button icon name (MUI icon key). */
  | "customActionIcon"
  /** @deprecated Use customActionIcon. */
  | "customActionStartIcon"
  /** Custom discipline action button variant. */
  | "customActionVariant"
  /** Custom discipline action button size. */
  | "customActionSize"
  /** Custom discipline action button color. */
  | "customActionColor"
  /** Custom discipline grid-icon tooltip text. */
  | "customActionTooltip"
  /** Custom discipline list item field update (ports are built in UI). */
  | "customListSetField"
  /** Custom discipline persistent list replacement (ports are built in UI). */
  | "customListSetRows"
  /** Custom discipline transient grid rows replacement (ports are built in UI). */
  | "customListSetViewRows"
  /** Custom discipline group field update (ports are built in UI). */
  | "customGroupSetField"
  /** Custom discipline group page opener (ports are built in UI). */
  | "customOpenGroup"
  /** Custom discipline current group selector (ports are built in UI). */
  | "customSetCurrentGroup"
  /** Custom discipline current athlete selector (ports are built in UI). */
  | "customSetCurrentAthlete"
  /** Custom discipline modal opener (ports are built in UI). */
  | "customOpenModal"
  /** Custom discipline modal closer (no extra ports). */
  | "customCloseModal"
  /** Print a Display Editor modal layout using the current data context. */
  | "customPrintModal"
  /** Run another configured action after previous mutations have been persisted. */
  | "customRunAction"
  /** Submit the current host form after Behavior has prepared its input values. */
  | "customSubmitForm"
  /** Invoke a bounded host integration with a Behavior-defined data mapping. */
  | "customGroupService"
  | "customIncrementAthleteCount"
  /** Custom discipline modal input option list override (input-node behavior only). */
  | "customInputOptions"
  /** Custom discipline reference-item filter result (`referenceFilterBehavior` graphs only; boolean output). */
  | "customEntryEligible"
  | "customEntryMinimum"
  | "customEntryMaximum"
  | "customEntryStep"
  | "customEntryAllowWhileCalled"
  | "customEntryUnlimitedChanges"
  | "customEntryReviewChanges"
  | "customEntryMaxChanges"
  | "customEntryTicketReference"
  | "customReferenceFilter"
  | EditorStyleActionKey;

/** Maps an action key to the expected value input type. */
export const ACTION_SET_KEY_VALUE_TYPE: Record<
  ActionSetKey,
  Exclude<VariableValueType, "object">
> = {
  timerStart: "string",
  timerStop: "string",
  timerResume: "string",
  timerReset: "string",
  timerSet: "string",
  timerDelete: "string",
  text: "string",
  location: "location",
  transform: "transform",
  setFilter: "boolean",
  setSort: "number",
  setListSource: "list",
  totalWeight: "number",
  barWeight: "number",
  iconColor: "color",
  iconSize: "number",
  customActionLabel: "string",
  customActionDisabled: "boolean",
  customActionIcon: "string",
  customActionStartIcon: "string",
  customActionVariant: "string",
  customActionSize: "string",
  customActionColor: "color",
  customActionTooltip: "string",
  customListSetField: "string",
  customListSetRows: "list",
  customListSetViewRows: "list",
  customGroupSetField: "string",
  customOpenGroup: "string",
  customSetCurrentGroup: "string",
  customSetCurrentAthlete: "string",
  customOpenModal: "string",
  customCloseModal: "string",
  customPrintModal: "string",
  customRunAction: "string",
  customSubmitForm: "string",
  customGroupService: "string",
  customIncrementAthleteCount: "string",
  customInputOptions: "list",
  customEntryEligible: "boolean",
  customEntryMinimum: "number",
  customEntryMaximum: "number",
  customEntryStep: "number",
  customEntryAllowWhileCalled: "boolean",
  customEntryUnlimitedChanges: "boolean",
  customEntryReviewChanges: "boolean",
  customEntryMaxChanges: "number",
  customEntryTicketReference: "string",
  customReferenceFilter: "boolean",
  visibility: "boolean",
  backgroundColor: "color",
  backgroundImage: "gradient",
  color: "color",
  filter: "filter",
  boxShadow: "boxShadow",
  textShadow: "textShadow",
  fontSize: "number",
  fontFamily: "fontFamily",
  fontWeight: "fontWeight",
  textAlign: "textAlign",
  textTransform: "textTransform",
  whiteSpace: "whiteSpace",
  textDecorationLine: "textDecorationLine",
  textDecorationColor: "textDecorationColor",
  textDecorationStyle: "textDecorationStyle",
  textDecorationThickness: "textDecorationThickness",
  border: "border",
  borderRadius: "borderRadius",
  opacity: "number",
  padding: "spacing",
  setBinding: "string",
};

/**
 * Side effect: sets a field on the owning layout node when flow runs through it.
 */
export interface ActionSetNode extends BaseNode {
  type: "action";
  kind: "set";
  key: ActionSetKey;
  /**
   * Whether composite inputs (`location` / `transform`) are split into dedicated ports.
   * This UI state is persisted in the graph payload.
   */
  expandedPorts?: boolean;
  ports: Port[];
}

/**
 * Animation blueprint (V1): opens a linear keyframe sequence (`Begin → KeyFrame+ → End`).
 * Global timing parameters only; no keyframes on this node.
 */
export interface AnimBeginActionNode extends BaseNode {
  type: "action";
  kind: "animBegin";
  ports: Port[];
}

/**
 * One keyframe stop: offset %, easing, and optional animated property values.
 */
export interface AnimKeyframeActionNode extends BaseNode {
  type: "action";
  kind: "animKeyframe";
  ports: Port[];
}

/**
 * Finalizes the animation payload and defers outgoing flow until delay + duration elapses.
 */
export interface AnimEndActionNode extends BaseNode {
  type: "action";
  kind: "animEnd";
  ports: Port[];
}

/**
 * Plays a named scene animation on the current node.
 */
export interface AnimPlayActionNode extends BaseNode {
  type: "action";
  kind: "animPlay";
  /** When true, the scene animation loops forever and the flow output port is unused. */
  playInfinite?: boolean;
  ports: Port[];
}

export interface LayoutListActionNode extends BaseNode {
  type: "action";
  kind: "createRowVariant";
  ports: Port[];
}

/**
 * Calls a scene function defined in the catalog.
 * Ports are generated dynamically from the function's inputs/outputs.
 */
export interface FunctionCallActionNode extends BaseNode {
  type: "action";
  kind: "call";
  functionId: string;
  ports: Port[];
}

/**
 * Sets a scene variable override at runtime.
 * Ports: flow in, typed value, flow out.
 */
export interface SetVariableActionNode extends BaseNode {
  type: "action";
  kind: "setVariable";
  variableId: string;
  ports: Port[];
}

/** All action node variants (set + declarative animation chain). */
export type ActionNode =
  | ActionSetNode
  | AnimBeginActionNode
  | AnimKeyframeActionNode
  | AnimEndActionNode
  | AnimPlayActionNode
  | LayoutListActionNode
  | FunctionCallActionNode
  | SetVariableActionNode;

/** Closed list of layout properties animatable via behavior keyframes (V1). */
export const ANIMATABLE_PROPERTIES = [
  "opacity",
  "x",
  "y",
  "scale",
  "scaleX",
  "scaleY",
  "rotation",
  "skewX",
  "skewY",
  "width",
  "height",
] as const;

export type AnimatableProperty = (typeof ANIMATABLE_PROPERTIES)[number];

/** CSS `animation-direction` subset for behavior animation payloads. */
export type AnimationDirection =
  | "normal"
  | "reverse"
  | "alternate"
  | "alternate-reverse";

/** CSS `animation-fill-mode` values for behavior animation payloads. */
export type AnimationFillMode = "none" | "forwards" | "backwards" | "both";

export type BehaviorNode =
  | OperatorNode
  | UnaryOperatorNode
  | BinaryOperatorNode
  | TernaryOperatorNode
  | VariableNode
  | VariableRefNode
  | EventNode
  | ControlNode
  | ActionNode;

export interface BehaviorConnection {
  id: string;
  fromNodeId: string;
  fromPortId: string;
  toNodeId: string;
  toPortId: string;
}

export interface BehaviorPortPosition {
  x: number;
  y: number;
}

export type OnPortClick = (nodeId: string, port: Port) => void;

// ── Node palette templates ────────────────────────────────────────────────────
// Templates are lightweight "factory recipes" - they describe what kind of node
// to create when dropped on the canvas.  They intentionally omit `id`, `x`, `y`
// and `ports` (computed by `buildNodeFromTemplate`). The structural link to the
// runtime node types is made explicit below via `Omit`.

/**
 * Template for an operator node dropped from the palette.
 *
 * Derives its structural shape from {@link UnaryOperatorNode} /
 * {@link BinaryOperatorNode} / {@link TernaryOperatorNode} - minus the
 * canvas-specific fields (`id`, `x`, `y`, `ports`) which are computed at drop
 * time by `buildNodeFromTemplate`.
 *
 * Two extra fields convey the port-type information that would otherwise be
 * embedded inside the `ports` array on the live node:
 * - `inputValueType` - shared type of all input ports
 * - `outputValueType` - type of the output port (comparison/logical → `"boolean"`,
 *   arithmetic → `"number"`, transform → `"string"`)
 */
export interface OperatorNodeTemplate
  extends Omit<OperatorNode, "id" | "x" | "y" | "ports"> {
  /** Shared type of the input port(s). */
  inputValueTypes: (PrimitiveValueType | "any")[];
  /** Type produced by the output port. */
  outputValueType: PrimitiveValueType;
}

/**
 * Template for a primitive (non-object) variable node.
 * Derives from {@link VariableNode} minus canvas-specific fields.
 * `objectSource` is optional - when set the variable reads a live scalar field
 * from {@link InjectedData} instead of holding a user-entered literal.
 */
export interface VariableNodeTemplate
  extends Omit<
    VariableNode,
    "id" | "x" | "y" | "ports" | "valueType" | "objectFieldPath"
  > {
  valueType: PrimitiveValueType;
  objectSource?: DataSourceId;
}

/**
 * Template for an object variable node.
 * `objectSource` is required because the whole point is to read a live object
 * from {@link InjectedData}; `objectFieldPath` is chosen by the user after drop.
 */
export interface ObjectVariableNodeTemplate
  extends Omit<VariableNode, "id" | "x" | "y" | "ports" | "objectFieldPath"> {
  valueType: "object";
  objectSource: DataSourceId | `@objectBase:${string}`;
}

/**
 * Template for an `onChange` event node.
 * `objectFieldPath` is omitted - it defaults to the first available path and
 * can be changed after the node is placed on the canvas.
 */
export interface EventNodeTemplate
  extends Omit<
    EventNode,
    "id" | "x" | "y" | "ports" | "objectFieldPath" | "objectSource"
  > {
  kind: "onChange";
  objectSource: DataSourceId;
}

/**
 * Template for an `onLoad` event node.
 * No data source is required - the event fires once when the layout node mounts.
 */
export interface OnLoadEventNodeTemplate
  extends Omit<
    EventNode,
    "id" | "x" | "y" | "ports" | "objectSource" | "objectFieldPath"
  > {
  kind: "onLoad";
}

/**
 * Template for an `onClick` event node.
 * Scoped to custom discipline action behaviors.
 */
export interface OnClickEventNodeTemplate
  extends Omit<
    EventNode,
    "id" | "x" | "y" | "ports" | "objectSource" | "objectFieldPath"
  > {
  kind: "onClick";
}

/**
 * Template for list transition event nodes (`onListEnter`, `onListExit`).
 * Scoped to layoutList row templates and their descendants only.
 */
export interface ListTransitionEventNodeTemplate
  extends Omit<
    EventNode,
    "id" | "x" | "y" | "ports" | "objectSource" | "objectFieldPath" | "target"
  > {
  kind: "onListEnter" | "onListExit";
}

/**
 * Template for list filter transition event nodes.
 * Can target any layoutList node, or default to the owner list.
 */
export interface ListFilterTransitionEventNodeTemplate
  extends Omit<
    EventNode,
    "id" | "x" | "y" | "ports" | "objectSource" | "objectFieldPath" | "target"
  > {
  kind: "onListFilterExit" | "onListFilterEnter";
}

/**
 * Template for a Control Room (régie) event node.
 * Scoped to node behaviors and custom event trigger graphs.
 * Fires when the runtime receives a matching shortcut signal.
 */
export interface ControlRoomEventNodeTemplate
  extends Omit<
    EventNode,
    | "id"
    | "x"
    | "y"
    | "ports"
    | "objectSource"
    | "objectFieldPath"
    | "signalName"
    | "signalDescription"
    | "shortcutKey1"
    | "shortcutKey2"
    | "shortcutKey3"
  > {
  kind: "onControlRoom";
}

/** Template for a control node. */
export interface ControlNodeTemplate
  extends Omit<ControlNode, "id" | "x" | "y" | "ports" | "dataValueType"> {}

/** Template for a generic set action node. */
export interface ActionSetNodeTemplate
  extends Omit<ActionSetNode, "id" | "x" | "y" | "ports" | "expandedPorts"> {}

/** Template: animation Begin node. */
export interface AnimBeginNodeTemplate
  extends Omit<AnimBeginActionNode, "id" | "x" | "y" | "ports"> {}

/** Template: animation KeyFrame node. */
export interface AnimKeyframeNodeTemplate
  extends Omit<AnimKeyframeActionNode, "id" | "x" | "y" | "ports"> {}

/** Template: animation End node. */
export interface AnimEndNodeTemplate
  extends Omit<AnimEndActionNode, "id" | "x" | "y" | "ports"> {}

/** Template: animation Play node. */
export interface AnimPlayNodeTemplate
  extends Omit<AnimPlayActionNode, "id" | "x" | "y" | "ports"> {}

export interface LayoutListActionNodeTemplate
  extends Omit<LayoutListActionNode, "id" | "x" | "y" | "ports"> {}

/** Template for a function call action node. */
export interface FunctionCallNodeTemplate
  extends Omit<FunctionCallActionNode, "id" | "x" | "y" | "ports"> {}

/** Template for a setVariable action node. */
export interface SetVariableNodeTemplate
  extends Omit<SetVariableActionNode, "id" | "x" | "y" | "ports"> {}

/** Template for a variable reference node. */
export interface VariableRefNodeTemplate
  extends Omit<VariableRefNode, "id" | "x" | "y" | "ports"> {}

/**
 * Template for a custom scene event node (`onCustomSceneEvent`).
 * References a catalog entry by `eventId`.
 */
export interface CustomSceneEventNodeTemplate
  extends Omit<
    EventNode,
    | "id"
    | "x"
    | "y"
    | "ports"
    | "objectSource"
    | "objectFieldPath"
    | "customEventId"
  > {
  kind: "onCustomSceneEvent";
  /** Catalog entry ID, mapped to `customEventId` on the placed node. */
  eventId: string;
}

export type NodeTemplate =
  | OperatorNodeTemplate
  | VariableNodeTemplate
  | ObjectVariableNodeTemplate
  | VariableRefNodeTemplate
  | EventNodeTemplate
  | OnLoadEventNodeTemplate
  | OnClickEventNodeTemplate
  | ListTransitionEventNodeTemplate
  | ListFilterTransitionEventNodeTemplate
  | ControlRoomEventNodeTemplate
  | CustomSceneEventNodeTemplate
  | ControlNodeTemplate
  | ActionSetNodeTemplate
  | AnimBeginNodeTemplate
  | AnimKeyframeNodeTemplate
  | AnimEndNodeTemplate
  | AnimPlayNodeTemplate
  | LayoutListActionNodeTemplate
  | FunctionCallNodeTemplate
  | SetVariableNodeTemplate;

/** Alias kept for backward compatibility and clarity at call sites. */
export type BehaviorDraggedNodeTemplate = NodeTemplate;

// ── Operator template catalogue ───────────────────────────────────────────────

const op = (
  operator: Operator,
  inputValueTypes: (PrimitiveValueType | "any")[],
  mode: "unary" | "binary" | "ternary",
  outputValueType: PrimitiveValueType,
): OperatorNodeTemplate => ({
  type: "operator",
  operator,
  inputValueTypes,
  mode,
  outputValueType,
});

/**
 * Canonical list of all operator templates, grouped by input type.
 * Used by the node palette to populate the Operators accordion.
 */
export const OPERATOR_NODE_TEMPLATES: Record<
  PrimitiveValueType,
  OperatorNodeTemplate[]
> = {
  number: [
    // Comparison - boolean output
    op("defined", ["number"], "unary", "boolean"),
    op("empty", ["number"], "unary", "boolean"),
    op("equals", ["number"], "binary", "boolean"),
    op("notEquals", ["number"], "binary", "boolean"),
    op("greaterThan", ["number"], "binary", "boolean"),
    op("lessThan", ["number"], "binary", "boolean"),
    op("greaterThanOrEqual", ["number"], "binary", "boolean"),
    op("lessThanOrEqual", ["number"], "binary", "boolean"),
    // Arithmetic - number output
    op("add", ["number"], "binary", "number"),
    op("subtract", ["number"], "binary", "number"),
    op("multiply", ["number"], "binary", "number"),
    op("divide", ["number"], "binary", "number"),
    op("modulo", ["number"], "binary", "number"),
    op("power", ["number"], "binary", "number"),
    op("round", ["number"], "binary", "number"),
    op("ceil", ["number"], "unary", "number"),
    op("factorial", ["number"], "unary", "number"),
    // Cast - number output
    op("castToString", ["number"], "unary", "string"),
    op("castToBoolean", ["number"], "unary", "boolean"),
    op("castToFontWeight", ["number"], "unary", "fontWeight"),
    op("ifElse", ["number", "number", "boolean"], "ternary", "number"),
  ],
  string: [
    // Comparison - boolean output
    op("defined", ["string"], "unary", "boolean"),
    op("empty", ["string"], "unary", "boolean"),
    op("equals", ["string"], "binary", "boolean"),
    op("notEquals", ["string"], "binary", "boolean"),
    op("contains", ["string"], "binary", "boolean"),
    op("notContains", ["string"], "binary", "boolean"),
    op("startsWith", ["string"], "binary", "boolean"),
    op("endsWith", ["string"], "binary", "boolean"),
    // Transform - string output
    op("concat", ["string"], "binary", "string"),
    op("chartAt", ["string", "number"], "binary", "string"),
    // Comparator sign - number output
    op("compare", ["string"], "binary", "number"),
    // 3-input replace: replace(source, search, replacement)
    op("replace", ["string"], "ternary", "string"),
    // String transform - string output
    op("toUpperCase", ["string"], "unary", "string"),
    op("toLowerCase", ["string"], "unary", "string"),
    op("capitalize", ["string"], "unary", "string"),
    // Cast - string output
    op("castToNumber", ["string"], "unary", "number"),
    op("castToBoolean", ["string"], "unary", "boolean"),
    op("castToColor", ["string"], "unary", "color"),
    op("castToGradient", ["string"], "unary", "gradient"),
    op("castToFilter", ["string"], "unary", "filter"),
    op("castToBoxShadow", ["string"], "unary", "boxShadow"),
    op("castToTextShadow", ["string"], "unary", "textShadow"),
    op("castToBorder", ["string"], "unary", "border"),
    op("castToBorderRadius", ["string"], "unary", "borderRadius"),
    op("castToFontFamily", ["string"], "unary", "fontFamily"),
    op("castToFontWeight", ["string"], "unary", "fontWeight"),
    op("castToTextAlign", ["string"], "unary", "textAlign"),
    op("castToTextTransform", ["string"], "unary", "textTransform"),
    op("castToWhiteSpace", ["string"], "unary", "whiteSpace"),
    op("castToObject", ["string"], "unary", "object"),
    op("ifElse", ["string", "string", "boolean"], "ternary", "string"),
  ],
  list: [
    op("mapItems", ["list", "any"], "binary", "list"),
    op("defined", ["list"], "unary", "boolean"),
    op("empty", ["list"], "unary", "boolean"),
    op("length", ["list"], "unary", "number"),
    op("isNotEmpty", ["list"], "unary", "boolean"),
    op("sum", ["list"], "unary", "number"),
    op("average", ["list"], "unary", "number"),
    op("min", ["list"], "unary", "number"),
    op("max", ["list"], "unary", "number"),
    op("castToString", ["list"], "unary", "string"),
    op("contains", ["list", "string"], "binary", "boolean"),
    op("equals", ["list"], "binary", "boolean"),
    op("notEquals", ["list"], "binary", "boolean"),
    op("join", ["list", "string"], "binary", "string"),
    op("push", ["list", "string"], "binary", "list"),
    op("remove", ["list", "number"], "binary", "list"),
    op("take", ["list", "number"], "binary", "list"),
    op("get", ["list", "number"], "binary", "string"),
    op("map", ["list", "string"], "binary", "list"),
    op("find", ["list", "string"], "binary", "string"),
    op("groupBy", ["list", "string"], "binary", "list"),
    op("aggregateGroups", ["list", "number"], "binary", "list"),
    op("findByCondition", ["list", "boolean"], "binary", "string"),
    op("filter", ["list", "boolean"], "binary", "list"),
    op("sort", ["list", "number"], "binary", "list"),
    op("sortBy", ["list", "list"], "binary", "list"),
    op("currentItem", ["list"], "unary", "string"),
    op("comparator", ["list"], "unary", "string"),
    op("findByPath", ["list", "string", "string"], "ternary", "string"),
    op("ifElse", ["list", "list", "boolean"], "ternary", "list"),
  ],
  object: [
    op("extendObject", ["object", "object"], "binary", "object"),
    op("pickObject", ["object", "list"], "binary", "object"),
    op("omitObject", ["object", "list"], "binary", "object"),
    op("mapValues", ["object", "any"], "binary", "object"),
    op("objectKeys", ["object"], "unary", "list"),
    op("objectValues", ["object"], "unary", "list"),
    op("objectEntries", ["object"], "unary", "list"),
    op("currentEntry", ["object"], "unary", "object"),
    op("setProperty", ["object", "string", "any"], "ternary", "object"),
    op("defined", ["object"], "unary", "boolean"),
    op("empty", ["object"], "unary", "boolean"),
    op("get", ["object"], "unary", "string"),
    op("getByPath", ["object", "string"], "binary", "string"),
    op("castToString", ["object"], "unary", "string"),
    op("ifElse", ["object", "object", "boolean"], "ternary", "object"),
  ],
  boolean: [
    op("equals", ["boolean"], "binary", "boolean"),
    op("notEquals", ["boolean"], "binary", "boolean"),
    op("not", ["boolean"], "unary", "boolean"),
    op("or", ["boolean"], "binary", "boolean"),
    op("and", ["boolean"], "binary", "boolean"),
    op("boolToSign", ["boolean", "number", "number"], "ternary", "number"),
    op("ifElse", ["boolean", "boolean", "boolean"], "ternary", "boolean"),
    // Cast - boolean output
    op("castToString", ["boolean"], "unary", "string"),
    op("castToNumber", ["boolean"], "unary", "number"),
  ],
  date: [
    op("dateDifference", ["date", "date"], "binary", "number"),
    op("isSame", ["date"], "binary", "boolean"),
    op("isAfter", ["date"], "binary", "boolean"),
    op("isBefore", ["date"], "binary", "boolean"),
    op("addDays", ["date", "number"], "binary", "date"),
    op("addMonths", ["date", "number"], "binary", "date"),
    op("addYears", ["date", "number"], "binary", "date"),
    op("addHours", ["date", "number"], "binary", "date"),
    op("addMinutes", ["date", "number"], "binary", "date"),
    op("addSeconds", ["date", "number"], "binary", "date"),
    op("getDay", ["date"], "unary", "number"),
    op("getMonth", ["date"], "unary", "number"),
    op("getYear", ["date"], "unary", "number"),
    op("getHour", ["date"], "unary", "number"),
    op("getMinute", ["date"], "unary", "number"),
    op("getSecond", ["date"], "unary", "number"),
    op("castToString", ["date"], "unary", "string"),
    op("ifElse", ["date", "date", "boolean"], "ternary", "date"),
  ],
  fontFamily: [
    op("defined", ["fontFamily"], "unary", "boolean"),
    op("empty", ["fontFamily"], "unary", "boolean"),
    op("equals", ["fontFamily"], "binary", "boolean"),
    op("notEquals", ["fontFamily"], "binary", "boolean"),
    op("castToString", ["fontFamily"], "unary", "string"),
    op(
      "ifElse",
      ["fontFamily", "fontFamily", "boolean"],
      "ternary",
      "fontFamily",
    ),
  ],
  fontWeight: [
    op("defined", ["fontWeight"], "unary", "boolean"),
    op("empty", ["fontWeight"], "unary", "boolean"),
    op("equals", ["fontWeight"], "binary", "boolean"),
    op("notEquals", ["fontWeight"], "binary", "boolean"),
    op("castToString", ["fontWeight"], "unary", "string"),
    op(
      "ifElse",
      ["fontWeight", "fontWeight", "boolean"],
      "ternary",
      "fontWeight",
    ),
  ],
  textAlign: [
    op("defined", ["textAlign"], "unary", "boolean"),
    op("empty", ["textAlign"], "unary", "boolean"),
    op("equals", ["textAlign"], "binary", "boolean"),
    op("notEquals", ["textAlign"], "binary", "boolean"),
    op("castToString", ["textAlign"], "unary", "string"),
    op("ifElse", ["textAlign", "textAlign", "boolean"], "ternary", "textAlign"),
  ],
  textTransform: [
    op("defined", ["textTransform"], "unary", "boolean"),
    op("empty", ["textTransform"], "unary", "boolean"),
    op("equals", ["textTransform"], "binary", "boolean"),
    op("notEquals", ["textTransform"], "binary", "boolean"),
    op("castToString", ["textTransform"], "unary", "string"),
    op(
      "ifElse",
      ["textTransform", "textTransform", "boolean"],
      "ternary",
      "textTransform",
    ),
  ],
  whiteSpace: [
    op("defined", ["whiteSpace"], "unary", "boolean"),
    op("empty", ["whiteSpace"], "unary", "boolean"),
    op("equals", ["whiteSpace"], "binary", "boolean"),
    op("notEquals", ["whiteSpace"], "binary", "boolean"),
    op("castToString", ["whiteSpace"], "unary", "string"),
    op(
      "ifElse",
      ["whiteSpace", "whiteSpace", "boolean"],
      "ternary",
      "whiteSpace",
    ),
  ],
  textDecorationLine: [
    op("defined", ["textDecorationLine"], "unary", "boolean"),
    op("empty", ["textDecorationLine"], "unary", "boolean"),
    op("equals", ["textDecorationLine"], "binary", "boolean"),
    op("notEquals", ["textDecorationLine"], "binary", "boolean"),
    op("castToString", ["textDecorationLine"], "unary", "string"),
    op("castToTextDecorationLine", ["string"], "unary", "textDecorationLine"),
    op(
      "ifElse",
      ["textDecorationLine", "textDecorationLine", "boolean"],
      "ternary",
      "textDecorationLine",
    ),
  ],
  textDecorationColor: [
    op("defined", ["textDecorationColor"], "unary", "boolean"),
    op("empty", ["textDecorationColor"], "unary", "boolean"),
    op("equals", ["textDecorationColor"], "binary", "boolean"),
    op("notEquals", ["textDecorationColor"], "binary", "boolean"),
    op("castToString", ["textDecorationColor"], "unary", "string"),
    op(
      "ifElse",
      ["textDecorationColor", "textDecorationColor", "boolean"],
      "ternary",
      "textDecorationColor",
    ),
  ],
  textDecorationStyle: [
    op("defined", ["textDecorationStyle"], "unary", "boolean"),
    op("empty", ["textDecorationStyle"], "unary", "boolean"),
    op("equals", ["textDecorationStyle"], "binary", "boolean"),
    op("notEquals", ["textDecorationStyle"], "binary", "boolean"),
    op("castToString", ["textDecorationStyle"], "unary", "string"),
    op("castToTextDecorationStyle", ["string"], "unary", "textDecorationStyle"),
    op(
      "ifElse",
      ["textDecorationStyle", "textDecorationStyle", "boolean"],
      "ternary",
      "textDecorationStyle",
    ),
  ],
  textDecorationThickness: [
    op("defined", ["textDecorationThickness"], "unary", "boolean"),
    op("empty", ["textDecorationThickness"], "unary", "boolean"),
    op("equals", ["textDecorationThickness"], "binary", "boolean"),
    op("notEquals", ["textDecorationThickness"], "binary", "boolean"),
    op("castToString", ["textDecorationThickness"], "unary", "string"),
    op(
      "ifElse",
      ["textDecorationThickness", "textDecorationThickness", "boolean"],
      "ternary",
      "textDecorationThickness",
    ),
  ],
  color: [
    op("defined", ["color"], "unary", "boolean"),
    op("empty", ["color"], "unary", "boolean"),
    op("equals", ["color"], "binary", "boolean"),
    op("notEquals", ["color"], "binary", "boolean"),
    op("withAlpha", ["color", "number"], "binary", "color"),
    op("mix", ["color", "color", "number"], "ternary", "color"),
    op("castToString", ["color"], "unary", "string"),
    op("ifElse", ["color", "color", "boolean"], "ternary", "color"),
  ],
  gradient: [
    op("defined", ["gradient"], "unary", "boolean"),
    op("empty", ["gradient"], "unary", "boolean"),
    op("equals", ["gradient"], "binary", "boolean"),
    op("notEquals", ["gradient"], "binary", "boolean"),
    op("castToString", ["gradient"], "unary", "string"),
    op("ifElse", ["gradient", "gradient", "boolean"], "ternary", "gradient"),
  ],
  filter: [
    op("defined", ["filter"], "unary", "boolean"),
    op("empty", ["filter"], "unary", "boolean"),
    op("equals", ["filter"], "binary", "boolean"),
    op("notEquals", ["filter"], "binary", "boolean"),
    op("castToString", ["filter"], "unary", "string"),
    op("ifElse", ["filter", "filter", "boolean"], "ternary", "filter"),
  ],
  boxShadow: [
    op("defined", ["boxShadow"], "unary", "boolean"),
    op("empty", ["boxShadow"], "unary", "boolean"),
    op("equals", ["boxShadow"], "binary", "boolean"),
    op("notEquals", ["boxShadow"], "binary", "boolean"),
    op("castToString", ["boxShadow"], "unary", "string"),
    op("ifElse", ["boxShadow", "boxShadow", "boolean"], "ternary", "boxShadow"),
  ],
  textShadow: [
    op("defined", ["textShadow"], "unary", "boolean"),
    op("empty", ["textShadow"], "unary", "boolean"),
    op("equals", ["textShadow"], "binary", "boolean"),
    op("notEquals", ["textShadow"], "binary", "boolean"),
    op("castToString", ["textShadow"], "unary", "string"),
    op(
      "ifElse",
      ["textShadow", "textShadow", "boolean"],
      "ternary",
      "textShadow",
    ),
  ],
  border: [op("ifElse", ["border", "border", "boolean"], "ternary", "border")],
  borderRadius: [
    op(
      "ifElse",
      ["borderRadius", "borderRadius", "boolean"],
      "ternary",
      "borderRadius",
    ),
  ],
  spacing: [
    op("ifElse", ["spacing", "spacing", "boolean"], "ternary", "spacing"),
  ],
  location: [
    op("ifElse", ["location", "location", "boolean"], "ternary", "location"),
  ],
  transform: [
    op("ifElse", ["transform", "transform", "boolean"], "ternary", "transform"),
  ],
  animationDirection: [
    op("defined", ["animationDirection"], "unary", "boolean"),
    op("empty", ["animationDirection"], "unary", "boolean"),
    op("equals", ["animationDirection"], "binary", "boolean"),
    op("notEquals", ["animationDirection"], "binary", "boolean"),
    op("castToString", ["animationDirection"], "unary", "string"),
    op(
      "ifElse",
      ["animationDirection", "animationDirection", "boolean"],
      "ternary",
      "animationDirection",
    ),
  ],
  animationFill: [
    op("defined", ["animationFill"], "unary", "boolean"),
    op("empty", ["animationFill"], "unary", "boolean"),
    op("equals", ["animationFill"], "binary", "boolean"),
    op("notEquals", ["animationFill"], "binary", "boolean"),
    op("castToString", ["animationFill"], "unary", "string"),
    op(
      "ifElse",
      ["animationFill", "animationFill", "boolean"],
      "ternary",
      "animationFill",
    ),
  ],
  animationEasing: [
    op("defined", ["animationEasing"], "unary", "boolean"),
    op("empty", ["animationEasing"], "unary", "boolean"),
    op("equals", ["animationEasing"], "binary", "boolean"),
    op("notEquals", ["animationEasing"], "binary", "boolean"),
    op("castToString", ["animationEasing"], "unary", "string"),
    op(
      "ifElse",
      ["animationEasing", "animationEasing", "boolean"],
      "ternary",
      "animationEasing",
    ),
  ],
};

/** Literal variable templates (user-entered value). */
export const VARIABLE_NODE_TEMPLATES: VariableNodeTemplate[] = [
  { type: "variable", valueType: "number" },
  { type: "variable", valueType: "string" },
  { type: "variable", valueType: "boolean" },
  { type: "variable", valueType: "list" },
  { type: "variable", valueType: "date" },
  { type: "variable", valueType: "fontFamily" },
  { type: "variable", valueType: "fontWeight" },
  { type: "variable", valueType: "textAlign" },
  { type: "variable", valueType: "textTransform" },
  { type: "variable", valueType: "whiteSpace" },
  { type: "variable", valueType: "textDecorationLine" },
  { type: "variable", valueType: "textDecorationColor" },
  { type: "variable", valueType: "textDecorationStyle" },
  { type: "variable", valueType: "textDecorationThickness" },
  { type: "variable", valueType: "color" },
  { type: "variable", valueType: "gradient" },
  { type: "variable", valueType: "filter" },
  { type: "variable", valueType: "boxShadow" },
  { type: "variable", valueType: "textShadow" },
  { type: "variable", valueType: "border" },
  { type: "variable", valueType: "borderRadius" },
  { type: "variable", valueType: "spacing" },
  { type: "variable", valueType: "location" },
  { type: "variable", valueType: "transform" },
  { type: "variable", valueType: "animationDirection" },
  { type: "variable", valueType: "animationFill" },
  { type: "variable", valueType: "animationEasing" },
];

/** Control node templates. */
export const CONTROL_NODE_TEMPLATES: ControlNodeTemplate[] = [
  { type: "control", controlType: "ifElse" },
  { type: "control", controlType: "wait" },
  { type: "control", controlType: "repeat" },
  { type: "control", controlType: "mergeFlows" },
  { type: "control", controlType: "eventOutput" },
  { type: "control", controlType: "functionBegin" },
  { type: "control", controlType: "functionEnd" },
  { type: "control", controlType: "variableBegin" },
  { type: "control", controlType: "variableEnd" },
];

export type BindingPath = {
  kind: "binding";
  path: string;
};

export type BindingLiteral = {
  kind: "literal";
  value: unknown;
};

export type BindingUnaryOperator = {
  kind: "unary";
  operator: UnaryOperator;
  input: BindingExpression;
  options?: BindingOperatorOptions;
};

export type BindingBinaryOperator = {
  kind: "binary";
  operator: BinaryOperator;
  input1: BindingExpression;
  input2: BindingExpression;
  options?: BindingOperatorOptions;
};

export type BindingTernaryOperator = {
  kind: "ternary";
  operator: TernaryOperator;
  input1: BindingExpression;
  input2: BindingExpression;
  input3: BindingExpression;
  options?: BindingOperatorOptions;
};
export type BindingExpression = BindingPath | BindingLiteral | BindingOperator;

export type BindingOperatorOptions = {
  dateDifferencePart?: DateDifferencePart;
  timerOutputPort?: string;
  datePartTextFormat?: "numeric" | "2-digit" | "narrow" | "short" | "long";
  input1ValueType?: string;
  input2ValueType?: string;
  /** Dot-path on each list element for binary `find` (binding evaluation). */
  objectFieldPath?: string;
  /**
   * For list operators that expose two output ports (`find`, `findByPath`,
   * `findByCondition`): selects whether the binding resolves to the matched
   * `item` (default) or the matching `index` (number, `-1` if not found).
   * Set per-port at binding-time by `OperatorBehaviorNode.getOutput`.
   */
  outputKind?: "item" | "index";
};

export type BindingOperator =
  | BindingUnaryOperator
  | BindingBinaryOperator
  | BindingTernaryOperator;
